import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { usePromoCountdown } from './usePromoCountdown';
import { PROMO_DISCOUNT_PRICE, PROMO_ORIGINAL_PRICE } from './promoBanner.constants';
import promoBannerBg from './assets/promo-banner-bg.png';
import { fromPromotion, fromSubscription, useAppDispatch } from '../../../../store';
import { SubscriptionSource } from '../../../../models';

const BANNER_HEIGHT = 60;

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    width: '100%',
    height: BANNER_HEIGHT,
    borderRadius: 8,
    backgroundColor: '#412ED5',
    backgroundImage: `url(${promoBannerBg})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    overflow: 'hidden',
  },
  content: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // The baked-in pink/yellow shadow occupies ~4px at the bottom of the image;
    // nudge content up so it stays centred within the purple band.
    paddingBottom: 4,
    padding: theme.spacing(0, 2.5),
  },
  leftGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  badge: {
    position: 'relative',
    width: 92,
    height: BANNER_HEIGHT,
    flexShrink: 0,
  },
  badgePill: {
    position: 'absolute',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 11.4,
    lineHeight: 1,
    letterSpacing: 0.5,
    padding: theme.spacing(0.5, 1),
    transform: 'rotate(-15deg)',
    whiteSpace: 'nowrap',
  },
  badgeLimited: {
    top: 8,
    left: 0,
    backgroundColor: '#0F8461',
    borderRadius: '8px 8px 8px 0',
  },
  badgeOffer: {
    top: 26,
    left: 20,
    backgroundColor: '#BF3DC2',
    borderRadius: '0 8px 8px 8px',
    zIndex: 1,
  },
  priceGroup: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  priceOriginal: {
    color: '#AEAFF5',
    fontSize: 14,
    fontWeight: 500,
    textDecoration: 'line-through',
  },
  priceDiscount: {
    color: '#ffffff',
    fontSize: 22,
    fontWeight: 700,
  },
  priceDesc: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  countdown: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
    backgroundColor: '#ffffff',
    border: '0.5px solid #99A1D1',
    borderRadius: 4,
    padding: theme.spacing(0.75, 1.5),
  },
  countdownLabel: {
    color: '#00786F',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  countdownTime: {
    color: '#5959EB',
    fontSize: 14,
    fontWeight: 700,
  },
}));

const pad = (value: number): string => String(value).padStart(2, '0');

interface PromoBannerProps {
  accountId: number;
}

export const PromoBanner = ({ accountId }: PromoBannerProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const timeLeft = usePromoCountdown(accountId);
  const trueSubscription = useSelector(
    fromSubscription.selectTrueSubscriptions(SubscriptionSource.RECURLY),
  );
  const activePromotion = useSelector(fromPromotion.selectActivePromotion);

  // Fetch the active promotion if it has not been loaded yet (e.g. user lands
  // directly on the Confirm Your Plan page without visiting the subscription page first).
  useEffect(() => {
    if (!activePromotion) {
      dispatch(fromPromotion.doFetchActivePromotion());
    }
  }, [activePromotion, dispatch]);

  // Banner only renders for new users (no existing True subscription) who are
  // eligible for an active discount promotion.
  if (!timeLeft || trueSubscription || !activePromotion) {
    return null;
  }

  const timeText = `${timeLeft.days} d : ${pad(timeLeft.hours)}h : ${pad(timeLeft.minutes)}m : ${pad(timeLeft.seconds)}s`;

  return (
    <Box id="plan-v2-promo-banner" className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.leftGroup}>
          {/* LIMITED / OFFER badge */}
          <Box className={classes.badge}>
            <Typography component="span" className={`${classes.badgePill} ${classes.badgeLimited}`}>
              <FormattedMessage id="page.plan.v2.promo.badge.limited" />
            </Typography>
            <Typography component="span" className={`${classes.badgePill} ${classes.badgeOffer}`}>
              <FormattedMessage id="page.plan.v2.promo.badge.offer" />
            </Typography>
          </Box>

          {/* Pricing */}
          <Box className={classes.priceGroup}>
            <Typography component="span" className={classes.priceOriginal}>
              {`$${PROMO_ORIGINAL_PRICE}`}
            </Typography>
            <Typography component="span" className={classes.priceDiscount}>
              {`$${PROMO_DISCOUNT_PRICE.toLocaleString('en-US')}`}
            </Typography>
            <Typography component="span" className={classes.priceDesc}>
              <FormattedMessage id="page.plan.v2.promo.price.desc" />
            </Typography>
          </Box>
        </Box>

        {/* Countdown */}
        <Box className={classes.countdown}>
          <Typography component="span" className={classes.countdownLabel}>
            <FormattedMessage id="page.plan.v2.promo.countdown.label" />
          </Typography>
          <Typography component="span" className={classes.countdownTime}>
            {timeText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
